/**
 * deck.gl FillStyleExtension shader patch — workaround for visgl/deck.gl#7326.
 *
 * The stock FillStyleExtension fragment shader uses mod() to emulate texture
 * wrapping within an atlas sub-rect. That breaks GPU implicit derivative
 * computation at tile boundaries, which texture() relies on to select a
 * mipmap level. Result: mipmaps disabled (deck.gl's default via
 * lodMaxClamp: 0) → aliasing at parcel zoom; mipmaps enabled → moiré at
 * every tile seam. Either way, pattern fills look terrible at parcel zoom.
 *
 * Workaround proposed in #7326 (still open as of 2026): drop the mod()
 * calls and use GL's native REPEAT wrap mode + mipmaps. Trade-off: only
 * ONE pattern per layer (the whole texture is the pattern; no atlas
 * sub-rect). For multi-pattern UI, use one Deck layer per pattern.
 *
 * Two gotchas you MUST get right:
 *   1. Mutate the shared fill module's inject (singleton imported by
 *      FillStyleExtension).
 *   2. Delete module.instance — luma.gl 9 caches normalizedInjections
 *      on first compile and silently ignores subsequent inject mutations
 *      unless you reset module.instance.
 *
 * See agent wiki tech/mapbox.md for full context, and PropertyScout
 * issue #268 for the production rationale.
 */

import {FillStyleExtension} from '@deck.gl/extensions';

let _installed = false;
let _installError = null;

/**
 * Locate the singleton fill shader module that FillStyleExtension uses.
 * The module is not exported directly; we extract it via getShaders()
 * with a faked layer context.
 */
function getOriginalFillModule() {
  try {
    const ext = new FillStyleExtension({pattern: true});
    const fakeMgr = {add: () => {}, invalidate: () => {}};
    const fakeLayer = {
      getAttributeManager: () => fakeMgr,
      state: {},
      props: {},
      context: {device: {createTexture: () => ({delete: () => {}})}},
      setState: () => {}
    };
    const shaders = ext.getShaders.call(fakeLayer, ext);
    return (shaders && shaders.modules || []).find(m => m && m.name === 'fill') || null;
  } catch (e) {
    return null;
  }
}

/**
 * Install the no-mod inject onto the shared fill module.
 * Idempotent: subsequent calls are no-ops.
 *
 * Must run before the first patterned Deck layer compiles its shader.
 * Already-compiled layers on the same page are unaffected (their compiled
 * program is already in GPU memory).
 *
 * @returns {boolean} true if installed (or previously installed), false on error.
 */
export function installNoModInject() {
  if (_installed) return true;
  if (_installError) return false;

  const m = getOriginalFillModule();
  if (!m) {
    _installError = new Error('PsDeckMaps: could not locate FillStyleExtension fill module');
    console.error(_installError);
    return false;
  }

  m.inject['fs:DECKGL_FILTER_COLOR'] = `
    if (fill.patternEnabled) {
      float scale = FILL_UV_SCALE * fill_patternPlacement.z;
      vec2 patternUV = fill_uv / scale;
      vec2 texCoords = fill_patternBounds.xy + fill_patternBounds.zw * patternUV;
      vec4 patternColor = texture(fill_patternTexture, texCoords);
      color.a *= patternColor.a;
      if (!fill.patternMask) {
        color.rgb = patternColor.rgb;
      }
    }
  `;

  // CRITICAL: luma.gl 9 caches normalizedInjections on module.instance after
  // first compile. Mutating inject without clearing instance is silently ignored.
  if (m.instance) {
    delete m.instance;
  }

  _installed = true;
  return true;
}

/**
 * Smoke check the actual compiled fragment shader of a built Deck layer
 * to confirm the patch made it into GLSL. Use after a patterned layer
 * has been rendered at least once.
 *
 * Returns {ok: boolean, hasModBug: boolean, hasFix: boolean, fsLength: number}.
 * If hasModBug is true OR hasFix is false, the patch did not take effect
 * — likely because luma.gl/deck.gl shader-assembler caching changed in a
 * future version. Log loudly and consider pinning versions.
 */
export function verifyPatchOnLayer(deckLayer) {
  try {
    const subLayers = deckLayer.getSubLayers ? deckLayer.getSubLayers() : [];
    const fillSub = subLayers.find(s => s.id && s.id.includes('-polygons-fill'));
    if (!fillSub) return {ok: false, reason: 'no fill sublayer'};
    const models = fillSub.getModels ? fillSub.getModels() : [];
    const model = models[0];
    if (!model) return {ok: false, reason: 'no model'};
    const fs = model.fs || '';
    const hasModBug = fs.includes('mod(mod(');
    const hasFix = fs.includes('fill_uv / scale');
    return {ok: !hasModBug && hasFix, hasModBug, hasFix, fsLength: fs.length};
  } catch (e) {
    return {ok: false, reason: e.message};
  }
}

export function isInstalled() {
  return _installed;
}
